import React from 'react';
import { useEffect, useState } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function StockReports() {
    const [formData, setFormData] = useState({
        stockList: [],
        search_key: '',
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        statusFilter: 'all'
    });

    const [filteredData, setFilteredData] = useState([]);
    
    const [expiringMedicines, setExpiringMedicines] = useState([]);
    const [showExpiryAlert, setShowExpiryAlert] = useState(false);
    const [isLoadingExpiry, setIsLoadingExpiry] = useState(false);

    // ══════════════════════════════════════════════════════
    // HISTORY MODAL STATE (Purchase + Sale + Closing Stock)
    // ══════════════════════════════════════════════════════
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyMedicine, setHistoryMedicine] = useState('');
    const [historyBatch, setHistoryBatch] = useState('');
    const [historyBatchFilter, setHistoryBatchFilter] = useState('all');
    const [historyActiveTab, setHistoryActiveTab] = useState('summary'); // summary | purchase | sale

    // Data from API
    const [purchaseRecords, setPurchaseRecords] = useState([]);
    const [saleRecords, setSaleRecords] = useState([]);
    const [batchSummary, setBatchSummary] = useState([]);
    const [overallSummary, setOverallSummary] = useState({});

    const initData = async () => {
        const storedData = sessionStorage.getItem("ldata");

        if (storedData) {
            const fd = new FormData();
            fd.append('from_date', formData.from_date);
            fd.append('to_date', formData.to_date);

            try {
                const response = await axios.post(
                    AppConst.BASE_URL + AppConst.getStockReportUrl, 
                    fd, 
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
                
                if (response.data.status) {
                    const records = response.data.records;
                    if (records != null && records !== undefined) {
                        setFormData(prev => ({
                            ...prev,
                            stockList: records
                        }));
                        setFilteredData(records);
                    }
                } else {
                    toast.error(response.data.message || "Failed to fetch stock data");
                }
            } catch (error) {
                console.error(error);
                toast.error("Error fetching stock reports");
            }
        }
    };

    const fetchExpiringMedicines = async () => {
        setIsLoadingExpiry(true);
        try {
            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getExpiringMedicinesUrl,
                {},
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.status) {
                const medicines = response.data.records || [];
                setExpiringMedicines(medicines);
                setShowExpiryAlert(true);
                
                if (medicines.length === 0) {
                    toast.info("No medicines expiring within 30 days");
                }
            } else {
                toast.error(response.data.message || "Failed to fetch expiring medicines");
            }
        } catch (error) {
            console.error("Error fetching expiring medicines:", error);
            toast.error("Error fetching expiring medicines");
        } finally {
            setIsLoadingExpiry(false);
        }
    };

    const exportExpiringMedicinesToPDF = () => {
        if (expiringMedicines.length === 0) {
            toast.warning("No expiring medicines to export");
            return;
        }

        try {
            const doc = new jsPDF('l', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('Medicines Expiring Within 30 Days', pageWidth / 2, 15, { align: 'center' });
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const today = new Date().toLocaleDateString('en-GB');
            doc.text(`Report Generated: ${today}`, pageWidth / 2, 22, { align: 'center' });
            
            const criticalCount = expiringMedicines.filter(m => m.days_remaining <= 7).length;
            const warningCount = expiringMedicines.filter(m => m.days_remaining > 7 && m.days_remaining <= 15).length;
            const noticeCount = expiringMedicines.filter(m => m.days_remaining > 15).length;
            
            doc.setFontSize(9);
            doc.setTextColor(220, 53, 69);
            doc.text(`Critical (≤7 days): ${criticalCount}`, 14, 30);
            doc.setTextColor(255, 193, 7);
            doc.text(`Warning (8-15 days): ${warningCount}`, 70, 30);
            doc.setTextColor(13, 110, 253);
            doc.text(`Notice (16-30 days): ${noticeCount}`, 140, 30);
            doc.setTextColor(0, 0, 0);
            doc.text(`Total: ${expiringMedicines.length}`, 210, 30);
            
            const tableHeaders = [
                'Sl#', 'Medicine Name', 'Supplier', 'Batch No', 
                'Package Qty', 'Loose Qty', 'Expiry Date', 'Days Left', 'Status'
            ];
            
            const tableBody = expiringMedicines.map((medicine, index) => {
                let status = '';
                if (medicine.days_remaining <= 7) status = 'Critical';
                else if (medicine.days_remaining <= 15) status = 'Warning';
                else status = 'Notice';
                
                return [
                    index + 1,
                    medicine.medicine_name || '-',
                    medicine.supplier_name || '-',
                    medicine.batch_no || '-',
                    medicine.package_qty || 0,
                    medicine.loose_qty || 0,
                    medicine.expiry_date || '-',
                    `${medicine.days_remaining} day${medicine.days_remaining !== 1 ? 's' : ''}`,
                    status
                ];
            });
            
            autoTable(doc, {
                startY: 35,
                head: [tableHeaders],
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak', halign: 'left' },
                headStyles: { fillColor: [64, 81, 137], textColor: 255, fontStyle: 'bold', halign: 'center' },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 10 },
                    1: { cellWidth: 55 },
                    2: { cellWidth: 45 },
                    3: { cellWidth: 30 },
                    4: { halign: 'center', cellWidth: 20 },
                    5: { halign: 'center', cellWidth: 20 },
                    6: { halign: 'center', cellWidth: 28 },
                    7: { halign: 'center', cellWidth: 22 },
                    8: { halign: 'center', cellWidth: 22 }
                },
                didParseCell: function(data) {
                    if (data.column.index === 8 && data.section === 'body') {
                        const status = data.cell.raw;
                        if (status === 'Critical') {
                            data.cell.styles.textColor = [220, 53, 69];
                            data.cell.styles.fontStyle = 'bold';
                        } else if (status === 'Warning') {
                            data.cell.styles.textColor = [255, 193, 7];
                            data.cell.styles.fontStyle = 'bold';
                        } else if (status === 'Notice') {
                            data.cell.styles.textColor = [13, 110, 253];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                    if (data.column.index === 7 && data.section === 'body') {
                        const rowIndex = data.row.index;
                        const daysRemaining = expiringMedicines[rowIndex]?.days_remaining || 0;
                        if (daysRemaining <= 7) {
                            data.cell.styles.textColor = [220, 53, 69];
                            data.cell.styles.fontStyle = 'bold';
                        } else if (daysRemaining <= 15) {
                            data.cell.styles.textColor = [255, 193, 7];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                },
                margin: { top: 35, right: 10, bottom: 20, left: 10 },
                didDrawPage: function(data) {
                    doc.setFontSize(8);
                    doc.setTextColor(128);
                    doc.text(
                        `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
                        pageWidth / 2, pageHeight - 10, { align: 'center' }
                    );
                }
            });
            
            const fileName = `Expiring_Medicines_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            toast.success("PDF exported successfully!");
        } catch (error) {
            console.error('PDF Export error:', error);
            toast.error("Failed to export PDF");
        }
    };

    // ══════════════════════════════════════════════════════
    // FETCH FULL HISTORY (Purchase + Sale + Closing Stock)
    // ══════════════════════════════════════════════════════
    const fetchMedicineHistory = async (medicineName, batchNo = '') => {
        if (!medicineName) {
            toast.error("Medicine name is required");
            return;
        }

        setHistoryLoading(true);
        setHistoryMedicine(medicineName);
        setHistoryBatch(batchNo);
        setHistoryBatchFilter(batchNo || 'all');
        setHistoryActiveTab('summary');
        setShowHistoryModal(true);

        try {
            const fd = new FormData();
            fd.append('medicine_name', medicineName);
            if (batchNo) {
                fd.append('batch_no', batchNo);
            }

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getMedicineSaleHistoryUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response.data.status) {
                setPurchaseRecords(response.data.purchase_records || []);
                setSaleRecords(response.data.sale_records || []);
                setBatchSummary(response.data.batch_summary || []);
                setOverallSummary(response.data.overall_summary || {});
            } else {
                toast.error(response.data.message || "Failed to fetch history");
                setPurchaseRecords([]);
                setSaleRecords([]);
                setBatchSummary([]);
                setOverallSummary({});
            }
        } catch (error) {
            console.error("Error fetching medicine history:", error);
            toast.error("Error fetching medicine history");
            setPurchaseRecords([]);
            setSaleRecords([]);
            setBatchSummary([]);
            setOverallSummary({});
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleHistoryBatchFilterChange = (batchValue) => {
        setHistoryBatchFilter(batchValue);
        if (batchValue === 'all') {
            fetchMedicineHistory(historyMedicine, '');
        } else {
            fetchMedicineHistory(historyMedicine, batchValue);
        }
    };

    const closeHistoryModal = () => {
        setShowHistoryModal(false);
        setPurchaseRecords([]);
        setSaleRecords([]);
        setBatchSummary([]);
        setOverallSummary({});
        setHistoryMedicine('');
        setHistoryBatch('');
        setHistoryBatchFilter('all');
        setHistoryActiveTab('summary');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleClearFilters = () => {
        const today = new Date().toISOString().split('T')[0];
        setFormData(prev => ({
            ...prev,
            search_key: "",
            from_date: today,
            to_date: today,
            statusFilter: 'all'
        }));
    };

    const handleStatusFilter = (status) => {
        setFormData(prev => ({
            ...prev,
            statusFilter: status
        }));
    };

    useEffect(() => {
        let filtered = formData.stockList;
        
        if (formData.search_key.trim() !== '') {
            const searchLower = formData.search_key.toLowerCase();
            filtered = filtered.filter(item => 
                item.purchase_supplier?.toLowerCase().includes(searchLower) ||
                item.purchase_item_medicine?.toLowerCase().includes(searchLower) ||
                item.purchase_item_batch?.toLowerCase().includes(searchLower)
            );
        }
        
        if (formData.statusFilter === 'active') {
            filtered = filtered.filter(item => 
                !isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
            );
        } else if (formData.statusFilter === 'expired') {
            filtered = filtered.filter(item => 
                isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
            );
        }
        
        setFilteredData(filtered);
    }, [formData.search_key, formData.stockList, formData.statusFilter]);

    useEffect(() => {
        initData();
    }, []);

    useEffect(() => {
        if (formData.from_date && formData.to_date) {
            initData();
        }
    }, [formData.from_date, formData.to_date]);

    const isExpired = (month, year) => {
        if (!month || !year) return false;
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();
        let expiryMonth = parseInt(month);
        let expiryYear = parseInt(year);
        if (expiryYear < 100) expiryYear = 2000 + expiryYear;
        if (expiryYear < currentYear) return true;
        else if (expiryYear === currentYear && expiryMonth < currentMonth) return true;
        return false;
    };

    const getMonthName = (monthNum) => {
        if (!monthNum) return '-';
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const index = parseInt(monthNum) - 1;
        return monthNames[index] || '-';
    };

    const formatExpiryDate = (month, year) => {
        if (!month || !year) return '-';
        let fullYear = parseInt(year);
        if (fullYear < 100) fullYear = 2000 + fullYear;
        return `${getMonthName(month)}-${fullYear}`;
    };

    const exportToCSV = () => {
        try {
            const headers = ['Sl No', 'Supplier', 'Medicine', 'Package Qty', 'Loose Qty', 'Batch No', 'Expiry Date', 'MRP', 'Status'];
            
            const rows = filteredData.map((item, index) => [
                index + 1,
                `"${(item.purchase_supplier || '-').replace(/"/g, '""')}"`,
                `"${(item.purchase_item_medicine || '-').replace(/"/g, '""')}"`,
                item.purchase_item_qty_stock || '0',
                item.purchase_item_tab_cap_stock || '0',
                `"${(item.purchase_item_batch || '-').replace(/"/g, '""')}"`,
                formatExpiryDate(item.purchase_item_expiry_month, item.purchase_item_expiry_year),
                parseFloat(item.purchase_item_mrp || 0).toFixed(2),
                isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year) ? 'Expired' : 'Not Expired'
            ]);
            
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `Stock_Report_${formData.from_date}_to_${formData.to_date}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("CSV exported successfully!");
        } catch (error) {
            console.error('Export error:', error);
            toast.error("Failed to export CSV");
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // ══════════════════════════════════════════════════════
    // DataTable Columns — "History" button instead of "Sales"
    // ══════════════════════════════════════════════════════
    const columns = [
        {
            name: 'Sl No',
            selector: (row, index) => index + 1,
            sortable: false,
            width: '70px',
            center: true
        },
        {
            name: 'Supplier Name',
            selector: row => row.purchase_supplier,
            sortable: true,
            grow: 2,
            wrap: true
        },
        {
            name: 'Invoice No',
            selector: row => row.purchase_invoice,
            sortable: true,
            width: '130px',
            cell: row => (
                <span className="badge bg-secondary-subtle text-secondary fs-6">
                    {row.purchase_invoice || '-'}
                </span>
            )
        },
        {
            name: 'Medicine Name',
            selector: row => row.purchase_item_medicine,
            sortable: true,
            grow: 2,
            wrap: true
        },
        {
            name: 'Action',
            center: true,
            cell: row => (
                <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    title={`View full history for ${row.purchase_item_medicine}`}
                    onClick={() => fetchMedicineHistory(row.purchase_item_medicine, row.purchase_item_batch || '')}
                >
                    <i className="ri-history-line me-1"></i>
                    History
                </button>
            ),
            width: '120px',
        },
        {
            name: 'Pkg Qty',
            selector: row => row.purchase_item_qty_stock || 0,
            sortable: true,
            width: '100px',
            center: true,
            cell: row => (
                <span className="badge bg-primary-subtle text-primary fs-6">
                    {row.purchase_item_qty_stock || 0}
                </span>
            )
        },
        {
            name: 'Loose Qty',
            selector: row => row.purchase_item_tab_cap_stock || 0,
            sortable: true,
            width: '100px',
            center: true,
            cell: row => (
                <span className="badge bg-info-subtle text-info fs-6">
                    {row.purchase_item_tab_cap_stock || 0}
                </span>
            )
        },
        {
            name: 'Batch No',
            selector: row => row.purchase_item_batch,
            sortable: true,
            width: '120px'
        },
        {
            name: 'Expiry Date',
            selector: row => formatExpiryDate(row.purchase_item_expiry_month, row.purchase_item_expiry_year),
            sortable: true,
            width: '120px',
            cell: row => (
                <span>
                    {formatExpiryDate(row.purchase_item_expiry_month, row.purchase_item_expiry_year)}
                </span>
            )
        },
        {
            name: 'MRP',
            selector: row => row.purchase_item_mrp,
            sortable: true,
            width: '100px',
            right: true,
            cell: row => {
                const mrp = parseFloat(row.purchase_item_mrp);
                return mrp > 0 ? `₹${mrp.toFixed(2)}` : '';
            }
        },
        {
            name: 'Status',
            width: '120px',
            center: true,
            cell: row => {
                const expired = isExpired(row.purchase_item_expiry_month, row.purchase_item_expiry_year);
                return (
                    <span className={`badge ${expired ? 'bg-danger' : 'bg-success'}`}>
                        {expired ? 'Expired' : 'Not Expired'}
                    </span>
                );
            }
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#405189',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                borderRadius: '5px 5px 0 0'
            }
        },
        headCells: {
            style: {
                color: '#fff',
                paddingLeft: '12px',
                paddingRight: '12px'
            }
        },
        rows: {
            style: {
                fontSize: '14px',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                }
            }
        }
    };

    // Get unique batches from batch summary for the dropdown
    const uniqueBatchesInHistory = batchSummary.map(b => b.batch_no);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-header border-0">
                                <div className="row align-items-center gy-3">
                                    <div className="col-sm">
                                        <h5 className="card-title mb-0">
                                            <i className="ri-file-list-3-line me-2"></i>
                                            Stock Reports
                                        </h5>
                                    </div>
                                    <div className="col-sm-auto">
                                        <div className="d-flex gap-2 flex-wrap">
                                            <button 
                                                type="button" 
                                                className="btn btn-warning"
                                                onClick={fetchExpiringMedicines}
                                                disabled={isLoadingExpiry}
                                            >
                                                <i className={`${isLoadingExpiry ? 'ri-loader-4-line rotating' : 'ri-alarm-warning-line'} align-bottom me-1`}></i> 
                                                {isLoadingExpiry ? 'Loading...' : 'Expiring Medicines'}
                                                {expiringMedicines.length > 0 && !isLoadingExpiry && (
                                                    <span className="badge bg-danger ms-2">{expiringMedicines.length}</span>
                                                )}
                                            </button>
                                            
                                            <button 
                                                type="button" 
                                                className="btn btn-success"
                                                onClick={exportToCSV}
                                                disabled={filteredData.length === 0}
                                            >
                                                <i className="ri-file-excel-line align-bottom me-1"></i> 
                                                Export Excel
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-info no-print"
                                                onClick={handlePrint}
                                                disabled={filteredData.length === 0}
                                            >
                                                <i className="ri-printer-line align-bottom me-1"></i> 
                                                Print
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="card-body border border-dashed border-end-0 border-start-0 no-print">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="form-label">From Date</label>
                                        <input 
                                            className="form-control" 
                                            type="date" 
                                            name="from_date"
                                            onChange={handleChange} 
                                            value={formData.from_date} 
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">To Date</label>
                                        <input 
                                            className="form-control" 
                                            type="date" 
                                            name="to_date"
                                            onChange={handleChange} 
                                            value={formData.to_date} 
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">&nbsp;</label>
                                        <div>
                                            <button 
                                                type="button" 
                                                onClick={handleClearFilters} 
                                                className="btn btn-info w-100"
                                            >
                                                <i className="ri-refresh-line me-1"></i> Clear
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Search</label>
                                        <input 
                                            className="form-control" 
                                            placeholder="Search by Supplier, Medicine or Batch..." 
                                            type="text"
                                            name="search_key" 
                                            onChange={handleChange} 
                                            value={formData.search_key} 
                                        />
                                    </div>
                                </div>
                                
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <label className="form-label">Filter by Status:</label>
                                        <div className="btn-group w-100" role="group">
                                            <button
                                                type="button"
                                                className={`btn ${formData.statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => handleStatusFilter('all')}
                                            >
                                                <i className="ri-list-check me-1"></i>
                                                All Products
                                                <span className="badge bg-light text-dark ms-2">
                                                    {formData.stockList.length}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${formData.statusFilter === 'active' ? 'btn-success' : 'btn-outline-success'}`}
                                                onClick={() => handleStatusFilter('active')}
                                            >
                                                <i className="ri-checkbox-circle-line me-1"></i>
                                                Active Products
                                                <span className="badge bg-light text-dark ms-2">
                                                    {formData.stockList.filter(item => 
                                                        !isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
                                                    ).length}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${formData.statusFilter === 'expired' ? 'btn-danger' : 'btn-outline-danger'}`}
                                                onClick={() => handleStatusFilter('expired')}
                                            >
                                                <i className="ri-close-circle-line me-1"></i>
                                                Expired Products
                                                <span className="badge bg-light text-dark ms-2">
                                                    {formData.stockList.filter(item => 
                                                        isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
                                                    ).length}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body pt-0">
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    customStyles={customStyles}
                                    pagination
                                    paginationPerPage={25}
                                    paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
                                    highlightOnHover
                                    responsive
                                    striped
                                    dense
                                    noDataComponent={
                                        <div className="text-center py-5">
                                            <i className="ri-inbox-line fs-1 text-muted"></i>
                                            <p className="mt-3">No stock records found</p>
                                        </div>
                                    }
                                />
                                
                                <div className="mt-3 p-3 bg-light rounded">
                                    <div className="row text-center">
                                        <div className="col-md-4">
                                            <strong>Total Records:</strong>
                                            <div className="fs-5 text-primary">{filteredData.length}</div>
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Expired Items:</strong>
                                            <div className="fs-5 text-danger">
                                                {filteredData.filter(item => 
                                                    isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
                                                ).length}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Active Items:</strong>
                                            <div className="fs-5 text-success">
                                                {filteredData.filter(item => 
                                                    !isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
                                                ).length}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* ══════════════════════════════════════════════════════════════
                FULL HISTORY MODAL — Purchase + Sale + Closing Stock
                ══════════════════════════════════════════════════════════════ */}
            {showHistoryModal && (
                <>
                    <div className="modal fade show" 
                         style={{ display: 'block', zIndex: 1060 }}
                         tabIndex="-1">
                        <div className="modal-dialog modal-xl modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header" style={{ backgroundColor: '#405189', color: '#fff' }}>
                                    <h5 className="modal-title">
                                        <i className="ri-history-line me-2"></i>
                                        Medicine History — {historyMedicine}
                                        {historyBatch && (
                                            <span className="badge bg-warning text-dark ms-2">
                                                Batch: {historyBatch}
                                            </span>
                                        )}
                                    </h5>
                                    <button 
                                        type="button" 
                                        className="btn-close btn-close-white" 
                                        onClick={closeHistoryModal}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {historyLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-3 text-muted">Loading history...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* ── Batch Filter ── */}
                                            <div className="row mb-3">
                                                <div className="col-md-4">
                                                    <label className="form-label fw-bold">
                                                        <i className="ri-filter-3-line me-1"></i>Filter by Batch
                                                    </label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={historyBatchFilter}
                                                        onChange={(e) => handleHistoryBatchFilterChange(e.target.value)}
                                                    >
                                                        <option value="all">All Batches ({overallSummary.total_batches || uniqueBatchesInHistory.length})</option>
                                                        {uniqueBatchesInHistory.map((batch, idx) => (
                                                            <option key={idx} value={batch}>{batch}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-8 d-flex align-items-end">
                                                    <small className="text-muted">
                                                        <strong>{purchaseRecords.length}</strong> purchase records, 
                                                        <strong className="ms-1">{saleRecords.length}</strong> sale records
                                                        {historyBatchFilter !== 'all' && (
                                                            <> for batch <strong>{historyBatchFilter}</strong></>
                                                        )}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* ── Overall Summary Cards ── */}
                                            <div className="row mb-3">
                                                <div className="col-md-2">
                                                    <div className="card border-primary mb-0">
                                                        <div className="card-body text-center py-2">
                                                            <small className="text-muted d-block">Purchased (Pkg)</small>
                                                            <h4 className="text-primary mb-0">
                                                                {overallSummary.total_purchase_pkg || 0}
                                                            </h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="card border-info mb-0">
                                                        <div className="card-body text-center py-2">
                                                            <small className="text-muted d-block">Purchased (Loose)</small>
                                                            <h4 className="text-info mb-0">
                                                                {overallSummary.total_purchase_loose || 0}
                                                            </h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="card border-danger mb-0">
                                                        <div className="card-body text-center py-2">
                                                            <small className="text-muted d-block">Sold (Pkg)</small>
                                                            <h4 className="text-danger mb-0">
                                                                {overallSummary.total_sale_pkg || 0}
                                                            </h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="card border-warning mb-0">
                                                        <div className="card-body text-center py-2">
                                                            <small className="text-muted d-block">Sold (Loose)</small>
                                                            <h4 className="text-warning mb-0">
                                                                {overallSummary.total_sale_loose || 0}
                                                            </h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="card border-success mb-0">
                                                        <div className="card-body text-center py-2">
                                                            <small className="text-muted d-block">Closing (Pkg)</small>
                                                            <h4 className="text-success mb-0 fw-bold">
                                                                {overallSummary.total_closing_pkg || 0}
                                                            </h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="card border-dark mb-0">
                                                        <div className="card-body text-center py-2">
                                                            <small className="text-muted d-block">Closing (Loose)</small>
                                                            <h4 className="text-dark mb-0 fw-bold">
                                                                {overallSummary.total_closing_loose || 0}
                                                            </h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ── Tab Navigation ── */}
                                            <ul className="nav nav-tabs nav-justified mb-3" role="tablist">
                                                <li className="nav-item">
                                                    <button 
                                                        className={`nav-link ${historyActiveTab === 'summary' ? 'active' : ''}`}
                                                        onClick={() => setHistoryActiveTab('summary')}
                                                    >
                                                        <i className="ri-pie-chart-line me-1"></i>
                                                        Batch Summary & Closing Stock
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button 
                                                        className={`nav-link ${historyActiveTab === 'purchase' ? 'active' : ''}`}
                                                        onClick={() => setHistoryActiveTab('purchase')}
                                                    >
                                                        <i className="ri-shopping-cart-line me-1"></i>
                                                        Purchase History
                                                        <span className="badge bg-primary ms-2">{purchaseRecords.length}</span>
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button 
                                                        className={`nav-link ${historyActiveTab === 'sale' ? 'active' : ''}`}
                                                        onClick={() => setHistoryActiveTab('sale')}
                                                    >
                                                        <i className="ri-shopping-bag-line me-1"></i>
                                                        Sale History
                                                        <span className="badge bg-danger ms-2">{saleRecords.length}</span>
                                                    </button>
                                                </li>
                                            </ul>

                                            {/* ════════════════════════════════════════
                                                TAB 1: BATCH SUMMARY + CLOSING STOCK
                                                ════════════════════════════════════════ */}
                                            {historyActiveTab === 'summary' && (
                                                <>
                                                    {batchSummary.length > 0 ? (
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-hover table-sm align-middle" style={{ fontSize: '13px' }}>
                                                                <thead style={{ backgroundColor: '#405189', color: '#fff' }}>
                                                                    <tr>
                                                                        <th rowSpan="2" className="text-center align-middle">Batch</th>
                                                                        <th rowSpan="2" className="text-center align-middle">Supplier</th>
                                                                        <th rowSpan="2" className="text-center align-middle">Expiry</th>
                                                                        <th rowSpan="2" className="text-center align-middle">MRP</th>
                                                                        <th colSpan="2" className="text-center" style={{ backgroundColor: '#0d6efd', color: '#fff' }}>Purchased</th>
                                                                        <th colSpan="2" className="text-center" style={{ backgroundColor: '#dc3545', color: '#fff' }}>Sold</th>
                                                                        <th colSpan="2" className="text-center" style={{ backgroundColor: '#198754', color: '#fff' }}>Closing Stock</th>
                                                                    </tr>
                                                                    <tr>
                                                                        <th className="text-center" style={{ backgroundColor: '#0d6efd', color: '#fff' }}>Pkg</th>
                                                                        <th className="text-center" style={{ backgroundColor: '#0d6efd', color: '#fff' }}>Loose</th>
                                                                        <th className="text-center" style={{ backgroundColor: '#dc3545', color: '#fff' }}>Pkg</th>
                                                                        <th className="text-center" style={{ backgroundColor: '#dc3545', color: '#fff' }}>Loose</th>
                                                                        <th className="text-center" style={{ backgroundColor: '#198754', color: '#fff' }}>Pkg</th>
                                                                        <th className="text-center" style={{ backgroundColor: '#198754', color: '#fff' }}>Loose</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {batchSummary.map((b, idx) => (
                                                                        <tr key={idx}>
                                                                            <td className="text-center">
                                                                                <span className="badge bg-info-subtle text-info">{b.batch_no}</span>
                                                                            </td>
                                                                            <td>{b.supplier || '-'}</td>
                                                                            <td className="text-center">{b.expiry || '-'}</td>
                                                                            <td className="text-end">₹{(b.mrp || 0).toFixed(2)}</td>
                                                                            {/* Purchased */}
                                                                            <td className="text-center">
                                                                                <span className="badge bg-primary">{b.purchase_pkg_qty || 0}</span>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <span className="badge bg-primary-subtle text-primary">{b.purchase_loose_qty || 0}</span>
                                                                            </td>
                                                                            {/* Sold */}
                                                                            <td className="text-center">
                                                                                <span className="badge bg-danger">{b.sale_pkg_qty || 0}</span>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <span className="badge bg-danger-subtle text-danger">{b.sale_loose_qty || 0}</span>
                                                                            </td>
                                                                            {/* Closing Stock */}
                                                                            <td className="text-center">
                                                                                <span className="badge bg-success fs-6 fw-bold">{b.closing_pkg_qty || 0}</span>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <span className="badge bg-success-subtle text-success fs-6 fw-bold">{b.closing_loose_qty || 0}</span>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                                <tfoot className="table-light">
                                                                    <tr className="fw-bold">
                                                                        <td colSpan="4" className="text-end">
                                                                            Total ({batchSummary.length} batch{batchSummary.length !== 1 ? 'es' : ''})
                                                                        </td>
                                                                        <td className="text-center">{overallSummary.total_purchase_pkg || 0}</td>
                                                                        <td className="text-center">{overallSummary.total_purchase_loose || 0}</td>
                                                                        <td className="text-center">{overallSummary.total_sale_pkg || 0}</td>
                                                                        <td className="text-center">{overallSummary.total_sale_loose || 0}</td>
                                                                        <td className="text-center text-success fs-6">{overallSummary.total_closing_pkg || 0}</td>
                                                                        <td className="text-center text-success fs-6">{overallSummary.total_closing_loose || 0}</td>
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <i className="ri-inbox-line" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                                            <p className="mt-3 text-muted">No batch data found</p>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* ════════════════════════════════════════
                                                TAB 2: PURCHASE HISTORY
                                                ════════════════════════════════════════ */}
                                            {historyActiveTab === 'purchase' && (
                                                <>
                                                    {purchaseRecords.length > 0 ? (
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-hover table-sm align-middle">
                                                                <thead style={{ backgroundColor: '#0d6efd', color: '#fff' }}>
                                                                    <tr>
                                                                        <th style={{ width: '50px' }}>Sl#</th>
                                                                        <th>Purchase Date</th>
                                                                        <th>Invoice No</th>
                                                                        <th>Supplier</th>
                                                                        <th>Batch</th>
                                                                        <th className="text-center">Qty</th>
                                                                        <th className="text-center">Free</th>
                                                                        <th className="text-center">Tab/Cap</th>
                                                                        <th className="text-center">Total Pkg</th>
                                                                        <th className="text-center">Total Loose</th>
                                                                        <th className="text-end">MRP</th>
                                                                        <th className="text-end">Pur Price</th>
                                                                        <th className="text-center">Expiry</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {purchaseRecords.map((pr, idx) => {
                                                                        const qty = parseFloat(pr.purchase_item_qty || 0);
                                                                        const free = parseFloat(pr.purchase_item_free || 0);
                                                                        const tabCap = parseInt(pr.purchase_item_tab_cap || 0);
                                                                        const totalPkg = qty + free;
                                                                        const totalLoose = tabCap * totalPkg;
                                                                        return (
                                                                            <tr key={idx}>
                                                                                <td className="text-center">{idx + 1}</td>
                                                                                <td>
                                                                                    {pr.purchase_date 
                                                                                        ? new Date(pr.purchase_date).toLocaleDateString('en-GB', {
                                                                                            day: '2-digit', month: 'short', year: 'numeric'
                                                                                        })
                                                                                        : '-'
                                                                                    }
                                                                                </td>
                                                                                <td>
                                                                                    <span className="badge bg-secondary">
                                                                                        {pr.purchase_invoice || '-'}
                                                                                    </span>
                                                                                </td>
                                                                                <td><strong>{pr.purchase_supplier || '-'}</strong></td>
                                                                                <td>
                                                                                    <span className="badge bg-info-subtle text-info">
                                                                                        {pr.purchase_item_batch || '-'}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="text-center">{qty}</td>
                                                                                <td className="text-center">
                                                                                    {free > 0 ? (
                                                                                        <span className="badge bg-success">{free}</span>
                                                                                    ) : '0'}
                                                                                </td>
                                                                                <td className="text-center">{tabCap}</td>
                                                                                <td className="text-center">
                                                                                    <span className="badge bg-primary fs-6">{totalPkg}</span>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <span className="badge bg-primary-subtle text-primary fs-6">{totalLoose}</span>
                                                                                </td>
                                                                                <td className="text-end">₹{parseFloat(pr.purchase_item_mrp || 0).toFixed(2)}</td>
                                                                                <td className="text-end">₹{parseFloat(pr.purchase_item_pur_price || 0).toFixed(2)}</td>
                                                                                <td className="text-center">
                                                                                    {formatExpiryDate(pr.purchase_item_expiry_month, pr.purchase_item_expiry_year)}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                                <tfoot className="table-light">
                                                                    <tr className="fw-bold">
                                                                        <td colSpan="8" className="text-end">Total Purchased:</td>
                                                                        <td className="text-center fs-6">{overallSummary.total_purchase_pkg || 0}</td>
                                                                        <td className="text-center fs-6">{overallSummary.total_purchase_loose || 0}</td>
                                                                        <td colSpan="3"></td>
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <i className="ri-shopping-cart-line" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                                            <p className="mt-3 text-muted">
                                                                No purchase history found for <strong>{historyMedicine}</strong>
                                                            </p>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* ════════════════════════════════════════
                                                TAB 3: SALE HISTORY
                                                ════════════════════════════════════════ */}
                                            {historyActiveTab === 'sale' && (
                                                <>
                                                    {saleRecords.length > 0 ? (
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-hover table-sm align-middle">
                                                                <thead style={{ backgroundColor: '#dc3545', color: '#fff' }}>
                                                                    <tr>
                                                                        <th style={{ width: '50px' }}>Sl#</th>
                                                                        <th>Bill No</th>
                                                                        <th>Sale Date</th>
                                                                        <th>Patient Name</th>
                                                                        <th>UHID</th>
                                                                        <th>Phone No</th>
                                                                        <th>Batch</th>
                                                                        <th className="text-center">Pack/Loose</th>
                                                                        <th className="text-center">Sale Qty</th>
                                                                        <th className="text-end">Amount</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {saleRecords.map((sale, idx) => (
                                                                        <tr key={idx}>
                                                                            <td className="text-center">{idx + 1}</td>
                                                                            <td>
                                                                                <span className="badge bg-secondary">
                                                                                    {String(sale.med_bill_id).padStart(3, '0')}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                {sale.med_bill_date 
                                                                                    ? new Date(sale.med_bill_date).toLocaleDateString('en-GB', {
                                                                                        day: '2-digit', month: 'short', year: 'numeric'
                                                                                    })
                                                                                    : '-'
                                                                                }
                                                                            </td>
                                                                            <td><strong>{sale.med_bill_patient_number || 'Walk-in'}</strong></td>
                                                                            <td>
                                                                                <span className="badge bg-primary-subtle text-primary">
                                                                                    {sale.med_bill_ipd_number || '-'}
                                                                                </span>
                                                                            </td>
                                                                            <td>{sale.med_bill_phone_number || '-'}</td>
                                                                            <td>
                                                                                <span className="badge bg-info-subtle text-info">
                                                                                    {sale.med_bill_item_batch || '-'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <span className={`badge ${
                                                                                    parseInt(sale.med_bill_item_is_loose) === 1 
                                                                                        ? 'bg-warning text-dark' 
                                                                                        : 'bg-success'
                                                                                }`}>
                                                                                    {parseInt(sale.med_bill_item_is_loose) === 1 ? 'Loose' : 'Package'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <strong className="fs-6">{sale.med_bill_item_qty}</strong>
                                                                            </td>
                                                                            <td className="text-end">
                                                                                ₹{parseFloat(sale.med_bill_item_total || 0).toFixed(2)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                                <tfoot className="table-light">
                                                                    <tr className="fw-bold">
                                                                        <td colSpan="7" className="text-end">Total Sold:</td>
                                                                        <td className="text-center">
                                                                            Pkg: {overallSummary.total_sale_pkg || 0} | 
                                                                            Loose: {overallSummary.total_sale_loose || 0}
                                                                        </td>
                                                                        <td className="text-center fs-6">
                                                                            {(overallSummary.total_sale_pkg || 0) + (overallSummary.total_sale_loose || 0)}
                                                                        </td>
                                                                        <td className="text-end">
                                                                            ₹{(overallSummary.total_sale_amt || 0).toFixed(2)}
                                                                        </td>
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <i className="ri-shopping-bag-line" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                                            <p className="mt-3 text-muted">
                                                                No sale history found for <strong>{historyMedicine}</strong>
                                                                {historyBatchFilter !== 'all' && (
                                                                    <> (Batch: <strong>{historyBatchFilter}</strong>)</>
                                                                )}
                                                            </p>
                                                            {historyBatchFilter !== 'all' && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary mt-2"
                                                                    onClick={() => handleHistoryBatchFilterChange('all')}
                                                                >
                                                                    <i className="ri-search-line me-1"></i> Search All Batches
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={closeHistoryModal}
                                    >
                                        <i className="ri-close-line me-1"></i> Close
                                    </button>
                                    {historyBatchFilter !== 'all' && (
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-info"
                                            onClick={() => handleHistoryBatchFilterChange('all')}
                                        >
                                            <i className="ri-stack-line me-1"></i> Show All Batches
                                        </button>
                                    )}
                                    <button 
                                        type="button" 
                                        className="btn btn-primary"
                                        onClick={() => fetchMedicineHistory(historyMedicine, historyBatchFilter === 'all' ? '' : historyBatchFilter)}
                                    >
                                        <i className="ri-refresh-line me-1"></i> Refresh
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1055 }}></div>
                </>
            )}

            {/* EXPIRING MEDICINES MODAL */}
            {showExpiryAlert && (
                <>
                    <div className="modal fade show" 
                         style={{ display: 'block' }}
                         tabIndex="-1">
                        <div className="modal-dialog modal-xl modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header bg-warning text-dark">
                                    <h5 className="modal-title">
                                        <i className="ri-alarm-warning-line me-2"></i>
                                        Medicines Expiring Within 30 Days ({expiringMedicines.length})
                                    </h5>
                                    <button 
                                        type="button" 
                                        className="btn-close" 
                                        onClick={() => setShowExpiryAlert(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {expiringMedicines.length > 0 ? (
                                        <>
                                            <div className="row mb-4">
                                                <div className="col-md-4">
                                                    <div className="card border-danger">
                                                        <div className="card-body text-center">
                                                            <h6 className="text-danger">
                                                                <i className="ri-error-warning-line"></i> Critical (≤7 days)
                                                            </h6>
                                                            <h3 className="text-danger mb-0">
                                                                {expiringMedicines.filter(m => m.days_remaining <= 7).length}
                                                            </h3>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="card border-warning">
                                                        <div className="card-body text-center">
                                                            <h6 className="text-warning">
                                                                <i className="ri-alert-line"></i> Warning (8-15 days)
                                                            </h6>
                                                            <h3 className="text-warning mb-0">
                                                                {expiringMedicines.filter(m => m.days_remaining > 7 && m.days_remaining <= 15).length}
                                                            </h3>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="card border-info">
                                                        <div className="card-body text-center">
                                                            <h6 className="text-info">
                                                                <i className="ri-information-line"></i> Notice (16-30 days)
                                                            </h6>
                                                            <h3 className="text-info mb-0">
                                                                {expiringMedicines.filter(m => m.days_remaining > 15).length}
                                                            </h3>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="table-responsive">
                                                <table className="table table-bordered table-hover">
                                                    <thead className="table-warning">
                                                        <tr>
                                                            <th>Sl. No.</th>
                                                            <th>Medicine Name</th>
                                                            <th>Supplier Name</th>
                                                            <th>Batch No</th>
                                                            <th>Package Qty</th>
                                                            <th>Loose Qty</th>
                                                            <th>Expiry Date</th>
                                                            <th>Days Remaining</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {expiringMedicines.map((medicine, index) => {
                                                            let statusClass = '';
                                                            let statusText = '';
                                                            let badgeClass = '';
                                                            
                                                            if (medicine.days_remaining <= 7) {
                                                                statusClass = 'table-danger';
                                                                statusText = 'Critical';
                                                                badgeClass = 'badge bg-danger';
                                                            } else if (medicine.days_remaining <= 15) {
                                                                statusClass = 'table-warning';
                                                                statusText = 'Warning';
                                                                badgeClass = 'badge bg-warning text-dark';
                                                            } else {
                                                                statusClass = 'table-info';
                                                                statusText = 'Notice';
                                                                badgeClass = 'badge bg-info text-dark';
                                                            }
                                                            
                                                            return (
                                                                <tr key={index} className={statusClass}>
                                                                    <td>{index + 1}</td>
                                                                    <td><strong>{medicine.medicine_name}</strong></td>
                                                                    <td>{medicine.supplier_name}</td>
                                                                    <td>
                                                                        <span className="badge bg-secondary">
                                                                            {medicine.batch_no}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className="badge bg-primary">
                                                                            {medicine.package_qty}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className="badge bg-success">
                                                                            {medicine.loose_qty}
                                                                        </span>
                                                                    </td>
                                                                    <td>{medicine.expiry_date}</td>
                                                                    <td className="text-center">
                                                                        <strong>
                                                                            {medicine.days_remaining} day{medicine.days_remaining !== 1 ? 's' : ''}
                                                                        </strong>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className={badgeClass}>
                                                                            {statusText}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="ri-check-line text-success" style={{ fontSize: '48px' }}></i>
                                            <p className="mt-3">No medicines expiring within 30 days</p>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowExpiryAlert(false)}
                                    >
                                        Close
                                    </button>
                                    
                                    {expiringMedicines.length > 0 && (
                                        <button 
                                            type="button" 
                                            className="btn btn-danger"
                                            onClick={exportExpiringMedicinesToPDF}
                                        >
                                            <i className="ri-file-pdf-line me-1"></i> Export PDF
                                        </button>
                                    )}
                                    
                                    <button 
                                        type="button" 
                                        className="btn btn-primary"
                                        onClick={() => {
                                            fetchExpiringMedicines();
                                            toast.success("Expiry data refreshed");
                                        }}
                                    >
                                        <i className="ri-refresh-line me-1"></i> Refresh
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
            
            {/* Print Styles */}
            <style>{`
                @media print {
                    .no-print {
                        display: none !important;
                    }
                    .card {
                        box-shadow: none !important;
                        border: 1px solid #ddd !important;
                    }
                    body {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                }
                
                .rotating {
                    animation: rotate 1s linear infinite;
                }
                
                @keyframes rotate {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </div>
    );
}

export default StockReports;